/**
 * Design System Preview - Shows proposed UI changes with real data
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useSageMode } from '../contexts/SageModeContext';
import {
  EyeIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import {
  Expandable,
  ExpandableCard,
  ExpandableCardHeader,
  ExpandableCardContent,
  ExpandableCardFooter,
  ExpandableContent,
  ExpandableTrigger,
} from '../components/ui/expandable';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  published_at: string | null;
  created_at: string;
}

export default function DesignSystemPreview() {
  const { state, exportReport } = useSageMode();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, content, published_at, created_at')
        .is('subdomain', null)
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPosts(data || []);
      if (data && data.length > 0) {
        setSelectedPost(data[0]);
      }
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Apply custom CSS if provided
  useEffect(() => {
    if (state.customCode) {
      const styleId = 'sage-mode-custom-css';
      let styleElement = document.getElementById(styleId);
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = state.customCode;
    }
    return () => {
      const styleElement = document.getElementById('sage-mode-custom-css');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [state.customCode]);

  const selectedComponents = state.selections.filter(s => s.selected);
  const blogCardSelected = selectedComponents.some(s => s.id === 'blog-card' || s.id === 'blog-expandable');

  const handleDeploy = async () => {
    if (!confirm('Are you sure you want to deploy these changes to production? This will update the live site.')) {
      return;
    }

    setDeploying(true);
    try {
      // Export the report
      const report = exportReport();

      // Here you would typically:
      // 1. Save the report to a database or file
      // 2. Trigger a deployment process
      // 3. Apply the custom code changes

      // For now, we'll create a deployment record
      const deploymentData = {
        timestamp: new Date().toISOString(),
        report: JSON.parse(report),
        customCode: state.customCode,
        selectedComponents: selectedComponents.map(c => c.id),
        status: 'pending',
      };

      // Save to localStorage as a deployment record
      const deployments = JSON.parse(localStorage.getItem('sageModeDeployments') || '[]');
      deployments.push(deploymentData);
      localStorage.setItem('sageModeDeployments', JSON.stringify(deployments));

      // Show success message
      alert('Deployment initiated! Changes will be applied to the preview. To deploy to production, the agent will need to review and apply the changes from the report.');

      // In a real implementation, this would trigger an API call to deploy
      // For now, we'll just mark it as ready for agent review
    } catch (error) {
      console.error('Deployment error:', error);
      alert('Error initiating deployment. Please try again.');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>THE LOST+UNFOUNDS | Design System Preview</title>
        <meta name="description" content="Internal design system preview surface. Not indexed." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.thelostandunfounds.com/designsystem/preview" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 tracking-wide flex items-center gap-3">
                <EyeIcon className="w-10 h-10 text-yellow-400" />
                DESIGN PREVIEW
              </h1>
              <p className="text-white/60 text-sm">
                Preview proposed UI changes with real blog post data
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/sagemode"
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-none text-white font-medium transition flex items-center gap-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to SAGE MODE
              </Link>
              {selectedComponents.length > 0 && (
                <button
                  onClick={handleDeploy}
                  disabled={deploying}
                  className="px-6 py-3 bg-[#008000] hover:bg-[#008000]/90 rounded-none text-white font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deploying ? (
                    <>
                      <RocketLaunchIcon className="w-4 h-4 animate-pulse" />
                      Deploying...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      Accept & Deploy Changes
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {selectedComponents.length > 0 && (
            <div className="bg-yellow-400/10 rounded-none p-4">
              <p className="text-yellow-400 text-sm">
                <strong>Preview Mode:</strong> Showing changes for {selectedComponents.length} selected component(s)
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-white/60">Loading preview data...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Post Selector */}
            {posts.length > 0 && (
              <div className="bg-black/50 rounded-none p-6">
                <h2 className="text-xl font-bold text-white mb-4 uppercase">SELECT BLOG POST TO PREVIEW</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {posts.map(post => (
                    <button
                      key={post.id}
                      onClick={() => setSelectedPost(post)}
                      className={`p-4 rounded-none text-left transition ${selectedPost?.id === post.id
                        ? 'bg-white/20 text-white'
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                        }`}
                    >
                      <div className="font-medium mb-1">{post.title}</div>
                      <div className="text-xs text-white/60">
                        {formatDate(post.published_at || post.created_at)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Area */}
            {selectedPost && (
              <div className="space-y-6">
                <div className="bg-black/50 rounded-none p-6">
                  <h2 className="text-xl font-bold text-white mb-4 uppercase">LIVE PREVIEW</h2>
                  <p className="text-white/60 text-sm mb-4">
                    This preview shows how the selected blog post would look with your proposed changes.
                    All interactions work as they would in production.
                  </p>

                  {/* Preview Container */}
                  <div className="bg-black rounded-none p-6">
                    {blogCardSelected ? (
                      // Show expandable card if selected
                      <div className="max-w-2xl mx-auto">
                        <Expandable
                          expandDirection="vertical"
                          expandBehavior="replace"
                          initialDelay={0}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {({ isExpanded }) => (
                            <ExpandableTrigger>
                              <div
                                className="rounded-none"
                                style={{
                                  minHeight: isExpanded ? '420px' : '220px',
                                  transition: 'min-height 0.2s ease-out',
                                }}
                              >
                                <ExpandableCard
                                  className="bg-black rounded-none h-full flex flex-col relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] cursor-pointer"
                                  collapsedSize={{ height: 220 }}
                                  expandedSize={{ height: 420 }}
                                  hoverToExpand={false}
                                  expandDelay={0}
                                  collapseDelay={0}
                                >
                                  <ExpandableCardHeader className="mb-1 pb-1">
                                    <h2 className="text-base font-black text-white mb-0 tracking-wide transition whitespace-nowrap overflow-hidden text-ellipsis">
                                      {selectedPost.title}
                                    </h2>
                                    <time className="text-white/60 text-xs font-medium block mt-1">
                                      {formatDate(selectedPost.published_at || selectedPost.created_at)}
                                    </time>
                                  </ExpandableCardHeader>

                                  <ExpandableCardContent className="flex-1 min-h-0">
                                    {selectedPost.excerpt && (
                                      <div className="mb-1">
                                        <p className="text-white/70 text-sm leading-relaxed line-clamp-4 text-left">
                                          {selectedPost.excerpt}
                                        </p>
                                      </div>
                                    )}

                                    <ExpandableContent
                                      preset="fade"
                                      stagger
                                      staggerChildren={0.1}
                                      keepMounted={false}
                                    >
                                      {selectedPost.content && (
                                        <div className="mb-2">
                                          <p className="text-white/60 text-xs leading-relaxed text-left line-clamp-6">
                                            {selectedPost.content.replace(/\n/g, ' ').substring(0, 300)}...
                                          </p>
                                        </div>
                                      )}
                                      <Link
                                        to={`/thelostarchives/${selectedPost.slug}`}
                                        className="inline-block mt-2 text-white/80 hover:text-white text-xs font-semibold transition"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Read Full Article →
                                      </Link>
                                    </ExpandableContent>
                                  </ExpandableCardContent>

                                  <ExpandableCardFooter className="mt-auto p-3 pt-2 pb-3">
                                    <div className="flex items-center justify-end gap-2 min-w-0 w-full">
                                      {!isExpanded && (
                                        <span className="text-white/90 text-xs font-semibold transition flex-shrink-0 whitespace-nowrap">
                                          Click to expand →
                                        </span>
                                      )}
                                    </div>
                                  </ExpandableCardFooter>
                                </ExpandableCard>
                              </div>
                            </ExpandableTrigger>
                          )}
                        </Expandable>
                      </div>
                    ) : (
                      // Default card view
                      <div className="max-w-2xl mx-auto">
                        <div className="bg-black rounded-none p-6">
                          <h2 className="text-2xl font-black text-white mb-4 tracking-wide">
                            {selectedPost.title}
                          </h2>
                          {selectedPost.excerpt && (
                            <p className="text-white/70 text-base leading-relaxed mb-4">
                              {selectedPost.excerpt}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <time className="text-white/70 text-sm">
                              {formatDate(selectedPost.published_at || selectedPost.created_at)}
                            </time>
                            <Link
                              to={`/thelostarchives/${selectedPost.slug}`}
                              className="text-white/80 hover:text-white text-sm font-semibold transition flex items-center gap-1"
                            >
                              Read Full Article
                              <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes for this component */}
                {selectedComponents.length > 0 && (
                  <div className="bg-black/50 rounded-none p-6">
                    <h3 className="text-lg font-bold text-white mb-4 uppercase">DESIGN NOTES</h3>
                    <div className="space-y-3">
                      {state.notes
                        .filter(note => selectedComponents.some(sc => sc.id === note.componentId))
                        .map(note => {
                          const component = state.selections.find(s => s.id === note.componentId);
                          return (
                            <div
                              key={note.id}
                              className="p-4 bg-white/5 rounded-none"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-white font-medium">{component?.name}</div>
                                <div className="text-white/40 text-xs">
                                  {new Date(note.timestamp).toLocaleString()}
                                </div>
                              </div>
                              {note.code && (
                                <pre className="text-xs text-white/60 bg-black/50 p-3 rounded-none mb-2 overflow-x-auto">
                                  {note.code}
                                </pre>
                              )}
                              <p className="text-white/70 text-sm">{note.notes}</p>
                            </div>
                          );
                        })}
                      {state.notes.filter(note => selectedComponents.some(sc => sc.id === note.componentId)).length === 0 && (
                        <p className="text-white/60 text-sm">No notes for selected components. Add notes in SAGE MODE.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
